import { useContext } from "react"
import { useQuery, useMutation, invalidateQuery } from "@blitzjs/rpc"
import { Routes } from "@blitzjs/next"
import Link from "next/link"
import Head from "next/head"
import { Crosshair2Icon, TrashIcon } from "@radix-ui/react-icons"
import getScenario from "app/scenarios/queries/getScenario"
import * as Collapsible from "@radix-ui/react-collapsible"
import { ScenarioName } from "./scenario_name"
import { ScenarioDelete } from "./scenario_delete"
import { formatNumber } from "app/utils/formatting_utils"
import { match } from "ts-pattern"
import { LngLatBoundsLike } from "mapbox-gl"
import { styledButton2 } from "./elements"
import deleteBlockgroupOnScenario from "app/blockgroup-on-scenarios/mutations/deleteBlockgroupOnScenario"
import deleteAreaOnScenario from "app/area-on-scenarios/mutations/deleteAreaOnScenario"
import { AdjustTrigger } from "./elements"
import { CityContext, MapContext } from "app/features/regional-map/regional-map.state"
import { getBbox } from "app/utils/scenario_utils"
import { getTreesNeededToReachTarget } from "app/utils/scenario_math"
import { scenarioIdEncode } from "app/utils/scenario_id"
import { ScenarioArea, ScenarioBlockgroup } from "types/data"
import getBlockgroupGeometry from "app/blockgroups/queries/getBlockgroupGeometry"
import getAreaGeometry from "app/areas/queries/getAreaGeometry"

type GetScenario = Awaited<ReturnType<typeof getScenario>>

function ScenarioItem({
  blockgroupOnScenario,
  scenario,
}: {
  blockgroupOnScenario: GetScenario["blockgroups"][0]
  scenario: GetScenario
}) {
  const [deleteBlockgroupOnScenarioMutation] = useMutation(deleteBlockgroupOnScenario)
  const [blockgroupGeometry] = useQuery(getBlockgroupGeometry, {
    id: blockgroupOnScenario.blockgroupId,
  })
  const map = useContext(MapContext)
  const city = useContext(CityContext)!
  const label = `${blockgroupOnScenario.name} #${blockgroupOnScenario.blockgroupId}`

  return (
    <div className="group flex justify-between gap-x-2 py-1 pl-3 pr-3 text-sm font-semibold text-gray-500 hover:bg-brand-green hover:bg-opacity-20 hover:text-black">
      <Link
        shallow
        href={Routes.Map({
          scenarioId: scenarioIdEncode(scenario.id),
          blockgroupId: blockgroupOnScenario.blockgroupId,
          city: city.id,
        })}
        className="group flex flex-auto items-center"
      >
        <div className="flex flex-auto items-center">
          <span>{label}</span>
        </div>
        <span className="opacity-0 transition-all group-hover:opacity-100">
          <AdjustTrigger />
        </span>
      </Link>
      <button
        className="text-brand-green-dark opacity-0 transition-opacity group-hover:opacity-100"
        type="button"
        title="Zoom to blockgroup"
        onClick={() => {
          map?.fitBounds(getBbox(blockgroupGeometry) as LngLatBoundsLike)
        }}
      >
        <Crosshair2Icon />
      </button>
      <button
        className="text-brand-green-dark opacity-0 transition-opacity group-hover:opacity-100"
        type="button"
        title="Delete"
        onClick={async () => {
          if (confirm("Are you sure you want to remove this blockgroup from this scenario?")) {
            await deleteBlockgroupOnScenarioMutation({
              scenarioId: scenario.id,
              blockgroupId: blockgroupOnScenario.blockgroupId,
            })
            await invalidateQuery(getScenario, { id: scenario.id })
          }
        }}
      >
        <TrashIcon />
      </button>
    </div>
  )
}

function SubItem({
  areaOnScenario,
  scenario,
}: {
  areaOnScenario: GetScenario["areas"][0]
  scenario: GetScenario
}) {
  const [deleteAreaOnScenarioMutation] = useMutation(deleteAreaOnScenario)
  const [areaGeometry] = useQuery(getAreaGeometry, { id: areaOnScenario.areaId })
  const map = useContext(MapContext)
  const city = useContext(CityContext)!
  const label = match<ScenarioArea>(areaOnScenario)
    .with(
      { area: { type: "RIGHT_OF_WAY" } },
      (areaOnScenario) =>
        `${areaOnScenario.name ? areaOnScenario.name : "Right-of-way"} ${
          areaOnScenario.area.address ? `| ${areaOnScenario.area.address.toLowerCase()}` : ""
        }`
    )
    .with(
      { area: { type: "PARCEL" } },
      (areaOnScenario) =>
        `${areaOnScenario.name ? areaOnScenario.name : "Parcel"} ${
          areaOnScenario.area.address ? `| ${areaOnScenario.area.address.toLowerCase()}` : ""
        }`
    )
    .exhaustive()
  return (
    <div className="group flex justify-between gap-x-2 py-0.5 pl-8 pr-3 text-sm text-gray-700 hover:bg-brand-green hover:bg-opacity-20 hover:text-black">
      <Link
        shallow
        href={Routes.Map({
          scenarioId: scenarioIdEncode(scenario.id),
          areaId: areaOnScenario.area.id,
          city: city.id,
        })}
        className="group flex flex-auto items-center"
      >
        <div className="flex-auto capitalize">{label}</div>
        <span className="opacity-0 transition-all group-hover:opacity-100">
          <AdjustTrigger />
        </span>
      </Link>
      <button
        className="text-brand-green-dark opacity-0 transition-opacity group-hover:opacity-100"
        type="button"
        title="Zoom to area"
        onClick={() => {
          map?.fitBounds(getBbox(areaGeometry) as LngLatBoundsLike)
        }}
      >
        <Crosshair2Icon />
      </button>
      <button
        className="text-brand-green-dark opacity-0 transition-opacity group-hover:opacity-100"
        type="button"
        title="Delete"
        onClick={async () => {
          await deleteAreaOnScenarioMutation({
            scenarioId: scenario.id,
            areaId: areaOnScenario.areaId,
          })
          await invalidateQuery(getScenario, { id: scenario.id })
        }}
      >
        <TrashIcon />
      </button>
    </div>
  )
}

export function ScenarioEditor({ scenarioId }: { scenarioId: number }) {
  const city = useContext(CityContext)!
  const [scenario] = useQuery(getScenario, {
    id: scenarioId,
  })
  const targetTrees = getTreesNeededToReachTarget(scenario.blockgroups)

  return (
    <>
      <Head>
        <title>{`TESA ${city?.shortTitle || ""} | ${scenario.name}`}</title>
      </Head>
      <div className="flex-auto">
        <Collapsible.Root defaultOpen>
          <div className="flex items-center justify-between">
            <ScenarioName scenario={scenario} />
            <ScenarioDelete id={scenario.id} />
          </div>
          {city.id === "austin" && (
            <div className="bg-[#3592BC] p-2 text-xs text-white">
              <strong>Notice:</strong> On 3/12, the Austin urban area was reclassified to the forest
              biome. Tree canopy goals and Tree Equity Scores were recalculated. This change may
              affect saved scenarios: if a user-created block group target was below the new score,
              it was reset to match the updated score. Related “trees needed” estimates were also
              recalculated. Please review saved block groups in your scenarios for any changes.
            </div>
          )}
          <Collapsible.Content className="divide-y divide-gray-300">
            <div className="bg-gray-100">
              {scenario.blockgroups.length ? (
                <>
                  <div className="space-y-1 py-4">
                    {scenario.blockgroups.map(
                      (blockgroupOnScenario: ScenarioBlockgroup, i: number) => (
                        <div key={i}>
                          <ScenarioItem
                            blockgroupOnScenario={blockgroupOnScenario}
                            scenario={scenario}
                          />
                          {scenario.areas
                            .filter((area: ScenarioArea) => {
                              return area.area.blockgroupId === blockgroupOnScenario.blockgroupId
                            })
                            .map((areaOnScenario: ScenarioArea, i: number) => {
                              return (
                                <SubItem
                                  key={i}
                                  areaOnScenario={areaOnScenario}
                                  scenario={scenario}
                                />
                              )
                            })}
                        </div>
                      )
                    )}
                  </div>
                </>
              ) : (
                <div className="flex justify-center p-4">
                  <div className="max-w-xs text-sm">
                    Click on an area on the map to start planting.
                  </div>
                </div>
              )}
            </div>
          </Collapsible.Content>
        </Collapsible.Root>
      </div>
      <div className="p-4">
        <div className="flex divide-x divide-gray-200">
          <div className="py-2 pr-4 text-xs font-semibold text-brand-green">
            Project
            <br />
            Planting
            <br />
            Overview
          </div>
          <div className="px-4 py-2">
            <div className="text-xl font-bold text-brand-green">
              {targetTrees.toLocaleString("en")}
            </div>
            <div className="text-xs text-gray-500">Trees Targeted</div>
          </div>
          <div className="px-4 py-2">
            <div className="text-xs text-gray-500">
              <span className="font-bold text-brand-green">
                {formatNumber(scenario.blockgroups.length)}
              </span>{" "}
              {city.locale && city.locale === "en-CA" ? "Census Tract" : "Block Group"}s
            </div>
            <div className="h-1" />
            <div className="text-xs text-gray-500">
              <span className="font-bold text-brand-green">
                {formatNumber(
                  scenario.areas.filter((area: ScenarioArea) => area.area.type === "PARCEL").length
                )}
              </span>{" "}
              Parcels
            </div>
            <div className="h-1" />
            <div className="text-xs text-gray-500">
              <span className="font-bold text-brand-green">
                {formatNumber(
                  scenario.areas.filter((area: ScenarioArea) => area.area.type === "RIGHT_OF_WAY")
                    .length
                )}
              </span>{" "}
              Rights-of-way
            </div>
          </div>
        </div>
        {scenario.blockgroups.length > 0 && (
          <div className="flex justify-center pb-2 pt-4">
            <Link
              href={Routes.ReportPage({ id: scenarioIdEncode(scenario.id), city: city.id })}
              className={styledButton2({ variant: "orange", size: "sm" })}
            >
              View Project Impact Report
            </Link>
          </div>
        )}
      </div>
    </>
  )
}
